import { useCallback, useRef, useState } from 'react';

import { useTranscriptSearchResultsContext } from '~/providers/TranscriptSearchResultsProvider';
import { type TranscriptSearchedItem } from '~/swr-hooks/clips/transcript/search/types';

type UseRealCurrentResultIndexParams = {
  onCurrentResultChange: (index: number) => void;
};
/**
 * This hook uses current result real index.
 * TranscriptSearch uses index in context of total results, but the app may not have all results fetched
 * This hook exposes onGoToNextResult/onGoTOPrevResult so that "total" index can be mapped to real index
 * Then, real index can be used to get correct item from results array
 */
export const useCurrentResultRealIndex = ({ onCurrentResultChange }: UseRealCurrentResultIndexParams) => {
  const { getSearchResults } = useTranscriptSearchResultsContext();
  const currentRealIndexRef = useRef<number>(-1);

  const [canGoToNext, setCanGoToNext] = useState(false);
  const [canGoToPrev, setCanGoToPrev] = useState(false);

  const onCurrentResultChangeRef = useRef(onCurrentResultChange);
  onCurrentResultChangeRef.current = onCurrentResultChange;

  const checkIfCanGoToNextResult = useCallback((results: TranscriptSearchedItem[]) => {
    const nextIndex = currentRealIndexRef.current >= results.length - 1 ? 0 : currentRealIndexRef.current + 1;
    const nextResult = results[nextIndex];
    const canGoToNext = !!nextResult && nextResult.type === 'result';
    setCanGoToNext(canGoToNext);
  }, []);

  const onGoToNextResult = useCallback(() => {
    const results = getSearchResults();
    currentRealIndexRef.current =
      currentRealIndexRef.current >= results.length - 1 ? 0 : currentRealIndexRef.current + 1;
    onCurrentResultChangeRef.current(currentRealIndexRef.current);
    checkIfCanGoToNextResult(results);
  }, [checkIfCanGoToNextResult, getSearchResults]);

  const checkIfCanGoToPrevResult = useCallback((results: TranscriptSearchedItem[]) => {
    const prevIndex = currentRealIndexRef.current <= 0 ? results.length - 1 : currentRealIndexRef.current - 1;
    const prevResult = results[prevIndex];
    const canGoToPrev = !!prevResult && prevResult.type === 'result';
    setCanGoToPrev(canGoToPrev);
  }, []);

  const onGoToPrevResult = useCallback(() => {
    const results = getSearchResults();
    currentRealIndexRef.current =
      currentRealIndexRef.current <= 0 ? results.length - 1 : currentRealIndexRef.current - 1;
    onCurrentResultChangeRef.current(currentRealIndexRef.current);
    checkIfCanGoToPrevResult(results);
  }, [checkIfCanGoToPrevResult, getSearchResults]);

  const resetCurrentRealIndex = useCallback(() => {
    currentRealIndexRef.current = -1;
    const results = getSearchResults();
    checkIfCanGoToNextResult(results);
    checkIfCanGoToPrevResult(results);
    onCurrentResultChangeRef.current(currentRealIndexRef.current);
  }, [checkIfCanGoToNextResult, checkIfCanGoToPrevResult, getSearchResults]);

  const recalculateCurrentRealIndex = useCallback(
    (newResults: TranscriptSearchedItem[]) => {
      const currentResults = getSearchResults();
      if (currentRealIndexRef.current !== -1 && currentResults.length > 0) {
        const currentResult = currentResults[currentRealIndexRef.current];
        currentRealIndexRef.current = newResults.findIndex(
          (result) => result.startTimeMs === currentResult.startTimeMs,
        );
      }
      checkIfCanGoToNextResult(newResults);
      checkIfCanGoToPrevResult(newResults);
    },
    [checkIfCanGoToNextResult, checkIfCanGoToPrevResult, getSearchResults],
  );

  const goToFirstResult = useCallback(() => {
    const results = getSearchResults();
    currentRealIndexRef.current = 0;
    onCurrentResultChangeRef.current(currentRealIndexRef.current);
    checkIfCanGoToNextResult(results);
    checkIfCanGoToPrevResult(results);
  }, [checkIfCanGoToNextResult, checkIfCanGoToPrevResult, getSearchResults]);

  return {
    onGoToNextResult: canGoToNext ? onGoToNextResult : undefined,
    onGoToPrevResult: canGoToPrev ? onGoToPrevResult : undefined,
    resetCurrentRealIndex,
    recalculateCurrentRealIndex,
    currentRealIndexRef,
    goToFirstResult,
  };
};
